import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CheckCircle2 } from 'lucide-react';
import Modal from './Modal';
import { CREATE_QUOTE_REQUEST } from '../graphql/mutations/quote';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  agencyId: number | null | undefined;
  agencyName?: string | null;
  primaryColor?: string;
}

interface FormState {
  contactNom: string;
  contactPrenom: string;
  contactEmail: string;
  contactTelephone: string;
  destination: string;
  travelDateStart: string;
  travelDateEnd: string;
  nbrAdulte: number;
  nbrEnfant: number;
  nbrBebe: number;
  budget: string;
  message: string;
}

const BUDGET_OPTIONS = [
  { value: '', label: '— Sélectionner —' },
  { value: 'Moins de 50 000 DA', label: 'Moins de 50 000 DA' },
  { value: '50 000 - 100 000 DA', label: '50 000 - 100 000 DA' },
  { value: '100 000 - 200 000 DA', label: '100 000 - 200 000 DA' },
  { value: '200 000 - 500 000 DA', label: '200 000 - 500 000 DA' },
  { value: 'Plus de 500 000 DA', label: 'Plus de 500 000 DA' },
];

const initialState: FormState = {
  contactNom: '',
  contactPrenom: '',
  contactEmail: '',
  contactTelephone: '',
  destination: '',
  travelDateStart: '',
  travelDateEnd: '',
  nbrAdulte: 1,
  nbrEnfant: 0,
  nbrBebe: 0,
  budget: '',
  message: '',
};

export default function QuoteRequestModal({
  isOpen,
  onClose,
  agencyId,
  agencyName,
  primaryColor,
}: Props) {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createQuoteRequest, { loading }] = useMutation(CREATE_QUOTE_REQUEST);

  const reset = () => {
    setForm(initialState);
    setSubmitted(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agencyId) {
      setError('Agence introuvable. Veuillez recharger la page.');
      return;
    }
    if (!form.contactNom.trim() || !form.contactPrenom.trim()) {
      setError('Nom et prénom sont requis.');
      return;
    }
    if (!form.contactEmail.trim() || !form.contactTelephone.trim()) {
      setError('Email et téléphone sont requis.');
      return;
    }

    try {
      await createQuoteRequest({
        variables: {
          input: {
            agencyId: Number(agencyId),
            contactNom: form.contactNom.trim(),
            contactPrenom: form.contactPrenom.trim(),
            contactEmail: form.contactEmail.trim(),
            contactTelephone: form.contactTelephone.trim(),
            destination: form.destination.trim() || null,
            travelDateStart: form.travelDateStart || null,
            travelDateEnd: form.travelDateEnd || null,
            nbrAdulte: Number(form.nbrAdulte) || 1,
            nbrEnfant: Number(form.nbrEnfant) || 0,
            nbrBebe: Number(form.nbrBebe) || 0,
            budget: form.budget || null,
            message: form.message.trim() || null,
          },
        },
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'envoi de la demande.");
    }
  };

  const btnStyle = primaryColor
    ? { backgroundColor: primaryColor }
    : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={submitted ? 'Demande envoyée' : 'Demande de devis'}
      wide
    >
      {submitted ? (
        <div className="py-6 text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto text-green-500 mb-3" />
          <p className="text-gray-800 font-medium mb-1">
            Merci, votre demande a bien été envoyée
            {agencyName ? ` à ${agencyName}` : ''}.
          </p>
          <p className="text-sm text-gray-600 mb-6">
            L'agence vous recontactera très prochainement par email ou téléphone.
          </p>
          <button
            onClick={handleClose}
            style={btnStyle}
            className="px-5 py-2 rounded-md text-white font-medium bg-blue-600 hover:opacity-90"
          >
            Fermer
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Remplissez ce formulaire — l'agence vous enverra un devis personnalisé.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Prénom *" value={form.contactPrenom} onChange={(v) => patch({ contactPrenom: v })} required />
            <Field label="Nom *" value={form.contactNom} onChange={(v) => patch({ contactNom: v })} required />
            <Field label="Email *" type="email" value={form.contactEmail} onChange={(v) => patch({ contactEmail: v })} required />
            <Field label="Téléphone *" value={form.contactTelephone} onChange={(v) => patch({ contactTelephone: v })} required />
            <Field label="Destination souhaitée" value={form.destination} onChange={(v) => patch({ destination: v })} full />
            <Field label="Date de départ" type="date" value={form.travelDateStart} onChange={(v) => patch({ travelDateStart: v })} />
            <Field label="Date de retour" type="date" value={form.travelDateEnd} onChange={(v) => patch({ travelDateEnd: v })} />
            <Field label="Nombre d'adultes" type="number" min={1} value={form.nbrAdulte} onChange={(v) => patch({ nbrAdulte: Number(v) || 1 })} />
            <Field label="Nombre d'enfants" type="number" min={0} value={form.nbrEnfant} onChange={(v) => patch({ nbrEnfant: Number(v) || 0 })} />
            <Field label="Nombre de bébés" type="number" min={0} value={form.nbrBebe} onChange={(v) => patch({ nbrBebe: Number(v) || 0 })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget approximatif</label>
              <select
                value={form.budget}
                onChange={(e) => patch({ budget: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                {BUDGET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Précisions (optionnel)</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => patch({ message: e.target.value })}
              placeholder="Type d'hébergement souhaité, vols depuis quelle ville, contraintes alimentaires..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={btnStyle}
              className="px-5 py-2 rounded-md text-white text-sm font-medium bg-blue-600 hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'Envoi...' : 'Envoyer la demande'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

interface FieldProps {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  full?: boolean;
  min?: number;
}

function Field({ label, value, onChange, type = 'text', required, full, min }: FieldProps) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value as any}
        min={min}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      />
    </div>
  );
}
